import React, { useState, useEffect } from 'react';
import '../styles/pages/_subscription.scss';
import '../styles/pages/_subscription-plans.scss';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG, isDevelopment } from '../config/api';
import { formatDate } from '../utils/dateFormatter';
import Button from '../components/Button';

interface Plan {
  _id: string;
  name: string;
  description: string;
  unit_name: string;
  token_rate: number;
  message_rate: number;
  currency: string;
  billing_interval: string;
  free_tokens_limit: number;
  free_messages_limit: number;
  price: number;
  sampleCosts: {
    lowUsage: number;
    mediumUsage: number;
    highUsage: number;
  };
  metadata?: {
    features?: string[];
  };
}

interface SubscriptionData {
  hasSubscription: boolean;
  subscription?: {
    id: string;
    status: string;
    plan: Plan;
    currentPeriod: {
      start: string;
      end: string;
      daysRemaining: number;
    };
    paymentMethod: {
      type: string;
    };
    billing: {
      estimatedAmount: number;
      currency: string;
    };
  };
  usage?: {
    currentPeriod: {
      totalTokens: number;
      totalMessages: number;
      billableTokens: number;
      billableMessages: number;
      periodCost: number;
      currency: string;
    };
    limits: {
      freeTokens: number;
      freeMessages: number;
    };
  };
}

const SubscriptionPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletingSubscription, setDeletingSubscription] = useState(false);
  const [activatingSubscription, setActivatingSubscription] = useState(false);

  // Check if user is admin - redirect if not
  React.useEffect(() => {
    if (user && user.role && user.role.toLowerCase() !== 'admin') {
      navigate('/panel/dashboard');
      return;
    }
  }, [user, navigate]);

  // Function to fetch plans and subscription data
  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch available plans
      const plansResponse = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_PLANS}?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache'
        }
      });

      if (!plansResponse.ok) {
        throw new Error('Failed to fetch plans');
      }

      const plansData = await plansResponse.json();
      setPlans(plansData.data || []);

      // Fetch user's subscription data
      const subscriptionResponse = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_SUBSCRIPTION}/me?t=${Date.now()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Cache-Control': 'no-cache'
        }
      });

      if (subscriptionResponse.ok) {
        const subscriptionData = await subscriptionResponse.json();
        setSubscriptionData(subscriptionData.data);
      } else if (subscriptionResponse.status === 404) {
        // No subscription found
        setSubscriptionData({ hasSubscription: false });
      } else {
        throw new Error('Failed to fetch subscription data');
      }
    } catch (err) {

      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Handle callback parameters from Xendit
  useEffect(() => {
    const setupStatus = searchParams.get('setup');
    const errorParam = searchParams.get('error');

    if (setupStatus === 'success') {
      setNotification({
        type: 'success',
        message: 'Payment method setup completed! Your subscription is now active.'
      });

      // Trigger subscription data refresh across components
      window.dispatchEvent(new CustomEvent('subscriptionRefresh'));
      localStorage.setItem('subscriptionRefresh', Date.now().toString());

      // Clear the URL parameters
      setSearchParams(new URLSearchParams());
    } else if (setupStatus === 'failed') {
      setNotification({
        type: 'error',
        message: 'Payment method setup failed. Please try again or contact support.'
      });
      // Clear the URL parameters
      setSearchParams(new URLSearchParams());
    } else if (errorParam === 'payment_failed') {
      setNotification({
        type: 'error',
        message: 'Payment failed. Please check your payment method and try again.'
      });
      // Clear the URL parameters
      setSearchParams(new URLSearchParams());
    }
  }, [searchParams, setSearchParams]);

  // Fetch plans and subscription data on mount/user change
  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  // Listen for subscription refresh events (e.g., after chat messages)
  useEffect(() => {
    const handleSubscriptionRefresh = () => {
      if (user) {
        console.log('Subscription refresh triggered, fetching updated data...');
        fetchData();
      }
    };

    window.addEventListener('subscriptionRefresh', handleSubscriptionRefresh);

    return () => {
      window.removeEventListener('subscriptionRefresh', handleSubscriptionRefresh);
    };
  }, [user]);

  // If not admin, don't render anything
  if (!user || !user.role || user.role.toLowerCase() !== 'admin') {
    return null;
  }

  // Generate features array from real plan data
  const generatePlanFeatures = (plan: Plan): string[] => {
    const features: string[] = [];

    // Add metadata features first (if available)
    if (plan.metadata?.features && Array.isArray(plan.metadata.features)) {
      features.push(...plan.metadata.features);
    }

    // Add real plan limits as features
    if (typeof plan.free_messages_limit === 'number' && plan.free_messages_limit >= 0) {
      features.push(`Up to ${plan.free_messages_limit.toLocaleString()} messages/month`);
    }

    if (typeof plan.free_tokens_limit === 'number' && plan.free_tokens_limit > 0) {
      features.push(`Up to ${plan.free_tokens_limit.toLocaleString()} tokens/month`);
    }

    // Note: Price information is displayed separately in the plan header, not in features list

    // If no features at all, add a basic message
    if (features.length === 0) {
      features.push('Basic access to all coaches');
    }

    return features;
  };

  const handleSubscribe = async (planId: string) => {
    // Check if user already has an active subscription
    if (subscriptionData?.hasSubscription) {
      setShowDuplicateAlert(true);
      return;
    }

    try {
      setLoading(true);

      const token = localStorage.getItem('token');
      console.log('Subscription init - User authenticated:', !!user);
      console.log('Subscription init - Token exists:', !!token);
      console.log('Subscription init - Plan ID:', planId);

      const response = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_SUBSCRIPTION}/init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ planId })
      });

      console.log('Subscription init - Response status:', response.status);
      console.log('Subscription init - Response ok:', response.ok);

      if (!response.ok) {
        // Try to get error details from response
        let errorMessage = 'Failed to initialize subscription';
        try {
          const errorData = await response.json();
          console.log('Subscription init - Error response:', errorData);
          // Use the backend error message if available
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          console.log('Subscription init - Could not parse error response');
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Handle real payment flow - redirect to Xendit payment page
      if (data.success && data.data?.setupUrl) {
        // Open payment page in new tab
        window.open(data.data.setupUrl, '_blank');

        // Refresh subscription data in case it was updated immediately
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('subscriptionRefresh'));
          localStorage.setItem('subscriptionRefresh', Date.now().toString());
        }, 1000);
      }
    } catch (err) {

      setError(err instanceof Error ? err.message : 'Failed to start subscription');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'PHP') => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };


  const formatBillingInterval = (interval: string) => {
    if (!interval) return 'month';
    return interval === 'month' ? 'month' : interval === 'year' ? 'year' : interval;
  };

  // Development mode: manually activate pending subscription
  const handleActivateSubscription = async () => {
    if (!subscriptionData?.hasSubscription || subscriptionData.subscription?.status === 'active') {
      return;
    }

    try {
      setActivatingSubscription(true);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_SUBSCRIPTION}/test/activate-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to activate subscription');
      }

      const result = await response.json();

      setNotification({
        type: 'success',
        message: 'Subscription activated successfully! (Development Mode)'
      });

      // Refresh subscription data
      setSubscriptionData(prev => prev ? {
        ...prev,
        subscription: {
          ...prev.subscription!,
          status: 'active'
        }
      } : null);

      // Trigger refresh across components
      window.dispatchEvent(new CustomEvent('subscriptionRefresh'));
      localStorage.setItem('subscriptionRefresh', Date.now().toString());

    } catch (error) {

      setNotification({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to activate subscription'
      });
    } finally {
      setActivatingSubscription(false);
    }
  };

  // Handle subscription deletion
  const handleDeleteSubscription = async () => {
    if (!subscriptionData?.subscription) return;

    try {
      setDeletingSubscription(true);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_SUBSCRIPTION}/${subscriptionData.subscription.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete subscription');
      }

      const result = await response.json();

      setNotification({
        type: 'success',
        message: 'Subscription deleted successfully'
      });

      // Close confirmation modal
      setShowDeleteConfirmation(false);

      // Refresh subscription data (should now show no subscription)
      await fetchData();

    } catch (error) {

      setNotification({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to delete subscription'
      });
    } finally {
      setDeletingSubscription(false);
    }
  };

  if (loading) {
    return (
      <div className="subscription-page">
        <div className="subscription-page__content">
          <div className="loading-spinner" style={{ textAlign: 'center', padding: '2rem' }}>
            Loading subscription information...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-page">
        <div className="subscription-page__content">
          <div className="error-message" style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-page">
      <div className="subscription-page__content">
        {/* Notification Messages */}
        {notification && (
          <div className={`notification notification--${notification.type}`}>
            <div className="notification__content">
              <span className="notification__message">{notification.message}</span>
              <button
                className="notification__close"
                onClick={() => setNotification(null)}
                aria-label="Close notification"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Delete Subscription Confirmation */}
        {showDeleteConfirmation && (
          <div className="modal-overlay" onClick={() => setShowDeleteConfirmation(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal__close" onClick={() => setShowDeleteConfirmation(false)}><span className="material-symbols-outlined">close</span></button>
              <div className="alert-modal">
                <h3>Delete Subscription</h3>
                <p>Are you sure you want to delete your subscription? This action cannot be undone.</p>
                <p><strong>Warning:</strong> This will permanently delete your subscription and all associated data. You will lose access to premium features immediately.</p>
                <div className="modal-buttons">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirmation(false)}
                    className="btn btn-secondary"
                    disabled={deletingSubscription}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteSubscription}
                    className="btn btn-danger"
                    disabled={deletingSubscription}
                  >
                    {deletingSubscription ? 'Deleting...' : 'Delete Subscription'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Duplicate Subscription Alert */}
        {showDuplicateAlert && (
          <div className="modal-overlay" onClick={() => setShowDuplicateAlert(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal__close" onClick={() => setShowDuplicateAlert(false)}><span className="material-symbols-outlined">close</span></button>
              <div className="alert-modal">
                <h3>Active Subscription Detected</h3>
                <p>You already have an active subscription. You cannot subscribe to multiple plans at the same time.</p>
                <p>If you want to change your plan, please contact support or wait for your current subscription to expire.</p>
                <div className="modal-buttons">
                  <button
                    type="button"
                    onClick={() => setShowDuplicateAlert(false)}
                    className="btn btn-primary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Current Subscription Status */}
        {subscriptionData?.hasSubscription && subscriptionData.subscription ? (
          <div className="subscription-content">
            {/* Page Title */}
            <div className="subscription-content__header">
              <h1 className="subscription-content__title">My Subscription</h1>
            </div>

            {/* Main Grid Layout */}
            <div className="subscription-grid">
              {/* Main Content - Left Column (2/3) */}
              <div className="subscription-grid__main">
                {/* Current Plan Card */}
                <div className="profile-card">
                  <div className="profile-card__header">
                    <h3 className="profile-card__title">Current Plan</h3>
                  </div>
                  <div className="profile-card__body">
                    <div className="subscription-plan-info">
                      <div className="subscription-plan-field">
                        <span className="subscription-plan-field__label">Plan Name</span>
                        <span className="subscription-plan-field__value">{subscriptionData.subscription.plan.name}</span>
                      </div>
                      <div className="subscription-plan-field">
                        <span className="subscription-plan-field__label">Subscription Status</span>
                        <span className={`subscription-status-badge subscription-status-badge--${subscriptionData.subscription.status}`}>
                          {subscriptionData.subscription.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className="subscription-plan-field">
                        <span className="subscription-plan-field__label">Current Billing Period</span>
                        <span className="subscription-plan-field__value">
                          {formatDate(subscriptionData.subscription.currentPeriod.start)} - {formatDate(subscriptionData.subscription.currentPeriod.end)}
                        </span>
                      </div>
                      <div className="subscription-plan-field">
                        <span className="subscription-plan-field__label">Days Remaining</span>
                        <span className="subscription-plan-field__value">
                          <span className={`days-remaining ${subscriptionData.subscription.currentPeriod.daysRemaining <= 3 ? 'days-remaining--critical' : subscriptionData.subscription.currentPeriod.daysRemaining <= 7 ? 'days-remaining--warning' : ''}`}>
                            {subscriptionData.subscription.currentPeriod.daysRemaining} day{subscriptionData.subscription.currentPeriod.daysRemaining !== 1 ? 's' : ''}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Usage Card */}
                {subscriptionData.usage && (
                  <div className="profile-card">
                    <div className="profile-card__header">
                      <h3 className="profile-card__title">Usage</h3>
                    </div>
                    <div className="profile-card__body">

                      <div className="usage-metrics-grid">
                        {/* Messages Usage */}
                        <div className="usage-metric">
                          <div className="usage-metric__header">
                            <span className="usage-metric__label">Messages Limit</span>
                            <span className="usage-metric__value">
                              {subscriptionData.usage.currentPeriod.totalMessages.toLocaleString()} / {subscriptionData.usage.limits.freeMessages.toLocaleString()}
                            </span>
                          </div>
                          <div className="progress-bar">
                            <div
                              className="progress-bar__fill"
                              style={{
                                width: `${Math.min((subscriptionData.usage.currentPeriod.totalMessages / subscriptionData.usage.limits.freeMessages) * 100, 100)}%`
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Billing Card */}
                <div className="profile-card">
                  <div className="profile-card__header">
                    <h3 className="profile-card__title">Billing</h3>
                  </div>
                  <div className="profile-card__body">
                    <div className="billing-info-grid">
                      <div className="billing-info-field">
                        <span className="billing-info-field__label">Next Billing Amount</span>
                        <span className="billing-info-field__value">
                          {formatCurrency(subscriptionData.subscription.billing.estimatedAmount, subscriptionData.subscription.billing.currency)}
                        </span>
                      </div>
                      <div className="billing-info-field">
                        <span className="billing-info-field__label">Next Renewal Date</span>
                        <span className="billing-info-field__value">
                          {formatDate(subscriptionData.subscription.currentPeriod.end)}
                        </span>
                      </div>
                    </div>

                    <div className="billing-history-link">
                      <Button
                        variant="secondary"
                        className="billing-history-link__button"
                        onClick={() => navigate('/panel/billing-history')}
                      >
                        View Billing History
                        <span className="material-symbols-outlined billing-history-link__icon">arrow_forward</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar - Right Column (1/3) */}
              <div className="subscription-grid__sidebar">
                {/* Actions Card */}
                <div className="profile-card">
                  <div className="profile-card__header">
                    <h3 className="profile-card__title">Actions</h3>
                  </div>
                  <div className="profile-card__body">
                    <div className="subscription-actions">
                      <button className="action-button action-button--primary">
                        Upgrade / Downgrade Plan
                      </button>
                      <button className="action-button action-button--secondary">
                        Update Payment Method
                      </button>
                      <div className="subscription-actions__divider"></div>
                      <button
                        className="action-button action-button--danger"
                        onClick={() => setShowDeleteConfirmation(true)}
                      >
                        Cancel Subscription
                      </button>
                    </div>
                  </div>
                </div>

                {/* Plan Features Card */}
                <div className="profile-card">
                  <div className="profile-card__header">
                    <h3 className="profile-card__title">Plan Features</h3>
                  </div>
                  <div className="profile-card__body">
                    <ul className="feature-list">
                      {generatePlanFeatures(subscriptionData.subscription.plan).map((feature, index) => (
                        <li key={index} className="feature-list__item">
                          <span className="material-symbols-outlined feature-list__icon">check_circle</span>
                          <span className="feature-list__text">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Development Mode Activation Button */}
            {subscriptionData.subscription.status === 'pending' && isDevelopment() && (
              <button className="dev-activation-button" onClick={handleActivateSubscription} disabled={activatingSubscription}>
                {activatingSubscription ? 'Activating...' : 'Activate (Dev)'}
              </button>
            )}
          </div>
        ) : (
          /* Plan Selection */
          <div className="subscription-plans">
            <div className="subscription-plans__header">
              <h2 className="subscription-plans__title">Choose Your Plan</h2>
              <p className="subscription-plans__subtitle">
                Pay only for what you use. All plans include free monthly usage limits.
              </p>
            </div>

            <div className="pricing-grid">
              {plans.map((plan) => (
                <div key={plan._id} className={`pricing-card ${plan.name === 'Pro' ? 'pricing-card--popular' : ''}`}>
                  {plan.name === 'Pro' && (
                    <div className="pricing-card__badge">
                      <span>Most Popular</span>
                    </div>
                  )}
                  <div className="pricing-card__content">
                    <h3 className="pricing-card__title">{plan.name}</h3>
                    <div className="pricing-card__price">
                      {plan.price && plan.price > 0 && plan.price < 99999 ? (
                        <>
                          <span className="pricing-card__price-amount">{formatCurrency(plan.price, plan.currency || 'PHP')}</span>
                          <span className="pricing-card__price-period">/{formatBillingInterval(plan.billing_interval || 'month')}</span>
                        </>
                      ) : (
                        <span className="pricing-card__price-amount">Contact us for the Price</span>
                      )}
                    </div>
                    <p className="pricing-card__description">{plan.description}</p>

                    <div className="subscription-plans__plan-features">
                      <h4 className="subscription-plans__features-title">Features:</h4>
                      <ul className="subscription-plans__features-list">
                        {generatePlanFeatures(plan).map((feature, index) => (
                          <li key={index} className="subscription-plans__feature-item">
                            <span className="material-symbols-outlined subscription-plans__feature-icon">check</span>
                            <span className="subscription-plans__feature-text">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {plan.price && plan.price > 0 && plan.price < 99999 ? (
                      <button
                        className={`pricing-card__button pricing-card__button--${plan.name.toLowerCase()}`}
                        onClick={() => handleSubscribe(plan._id)}
                        disabled={loading}
                      >
                        {loading ? 'Setting up...' : `Choose ${plan.name}`}
                      </button>
                    ) : (
                      <a
                        href="https://www.magiteque.life/contact-us//"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`pricing-card__button pricing-card__button--${plan.name.toLowerCase()}`}
                      >
                        Choose {plan.name}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;

